import type { ConfiguratorConfiguration } from "../../contract/configuration";
import type { ConfigurationSelectionEntry, ConfigurationSelectionModel } from "../../engine/selection";

export interface ConfiguratorPreviewRowViewModel {
  key: string;
  label: string;
  value: string;
}

export interface ConfiguratorPreviewSectionViewModel {
  key: string;
  title: string;
  rows: readonly ConfiguratorPreviewRowViewModel[];
}

export interface ConfiguratorPreviewViewModel {
  designation?: string;
  description?: string;
  imageUrl?: string;
  sections: readonly ConfiguratorPreviewSectionViewModel[];
}

export interface ConfiguratorSummaryViewModel extends ConfiguratorPreviewViewModel {
  pnaConfiguration: Pick<
    ConfiguratorConfiguration,
    "productCode" | "configId" | "priceFingerprint" | "availabilityFingerprint"
  >;
}

interface ConfiguratorPreviewOptions {
  translate: (key: string) => string;
  translateStepName?: (key: string) => string;
  standardLabel: string;
}

const formatEntryValue = (
  entry: ConfigurationSelectionEntry,
  { translate, standardLabel }: ConfiguratorPreviewOptions,
) => {
  const translatedValue = translate(entry.valueLabelKey ?? entry.value);
  const valueWithUnit = entry.unit ? `${translatedValue}${entry.unit}` : translatedValue;
  return entry.standard ? `${valueWithUnit} (${standardLabel})` : valueWithUnit;
};

export const buildConfiguratorPreviewViewModel = (
  configuration: ConfiguratorConfiguration,
  selections: ConfigurationSelectionModel,
  options: ConfiguratorPreviewOptions,
): ConfiguratorSummaryViewModel => ({
  pnaConfiguration: {
    productCode: configuration.productCode,
    configId: configuration.configId,
    priceFingerprint: configuration.priceFingerprint,
    availabilityFingerprint: configuration.availabilityFingerprint,
  },
  designation: configuration.productShortDesignation,
  description: configuration.description,
  imageUrl: configuration.summaryImageUrls[0],
  sections: selections.steps.flatMap((step) => {
    const rows = step.instances.flatMap((instance) =>
      instance.entries
        .filter((entry) => entry.miniSummary)
        .map((entry) => ({
          key: `${instance.instanceId}-${entry.controlIndex}-${entry.csticName}`,
          label: entry.summaryLabel,
          value: formatEntryValue(entry, options),
        })),
    );

    return rows.length > 0
      ? [
          {
            key: String(step.stepIndex),
            title: (options.translateStepName ?? options.translate)(step.titleKey),
            rows,
          },
        ]
      : [];
  }),
});
